import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import type { TableProps } from '@app/src/components/Table'
import Table from '@app/src/components/Table'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import { getResponseExportColumnsAccessor } from '@app/src/export-columns/response'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import ResponsesFilters from '@app/src/pages/ResourceCollection/Filters/ResponsesFilters'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { UseSort } from '@app/src/types/filter'
import type { Response } from '@app/src/types/resourceExplorer'
import {
  createNonScreenedResponseFilter,
  enhanceFiltersWithVerificationStatus,
} from '@app/src/utils/responseFilterHelpers'
import { ResourceTypes, WfRequestTags } from '@app/src/wf-constants'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface BaseAccessorResponseSceneProps {
  RowComponent: TableProps<Response>['RowComponent']
  HeaderComponent: React.FC<{
    toggleSorting: UseSort['toggleSorting']
    activeSorting: UseSort['sorting']
    showTargetProductColumn?: boolean
  }>
  relatedContentFilter?: FilterGroup[]
  embedded?: boolean
  tabs?: ResourceCollectionSceneProps['tabs']
  actionButtons?: ResourceCollectionSceneProps['actionButtons']
}

const allowedFilters = [
  'request.template.id',
  'externalVerificationStatus',
  'request.subscriptions.target.organization.countryId',
  'request.subscriptions.target.id',
  'request.periodName',
  'submittedAt',
  'internalVerificationStatus',
  'request.subscriptions.target.providerApprovalStatus',
  'request.subscriptions.target.priority',
  'request.subscriptions.target.finalRiskRating',
  'request.subscriptions.target.activityStatus',
  'request.subscriptions.target.supplierUsage',
  'request.subscriptions.target.tier',
  'request.subscriptions.target.providerOwners.userId',
  'request.subscriptions.target.providerOwners.user',
  'request.subscriptions.target.categoryOptions.id',
  'request.subscriptions.target.spends.spendClassification',
  'request.subscriptions.target.spends.periodName',
  'request.inquiries.status',
  'request.requestStatus',
  'verifications.externalVerificationStatus',
  'request.subscriptions.target.countryId',
  'verifications.internalVerificationStatus',
  'request.subscriptions.target.assessments.totalLevel',
  'request.subscriptions.target.assessments.section1Level',
  'request.subscriptions.target.assessments.section2Level',
  'request.subscriptions.target.assessments.section3Level',
  'request.subscriptions.target.assessments.section4Level',
  'request.subscriptions.target.assessments.section5Level',
  'request.subscriptions.target.assessments.assessmentTemplate.name',
  'request.subscriptions.target.assessments.assessmentTemplate.id',
  'request.subscriptions.assessmentName',
  'request.subscriptions.totalLevel',
  'request.subscriptions.section1Level',
  'request.subscriptions.section2Level',
  'request.subscriptions.section3Level',
  'request.subscriptions.section4Level',
  'request.subscriptions.section5Level',
  'request.subscriptions.targetType',
  'request.subscriptions.targetProductId',
  'hasExpiredFiles',
  'request.target.contacts.user',
  'request.subscriptions.target.mappingNodes.actorType.id',
  'request.subscriptions.target.mappingNodes.tier',
  'request.subscriptions.target.mappingNodes.product.productStatus',
]

const BaseAccessorResponseScene: React.FC<BaseAccessorResponseSceneProps> = ({
  HeaderComponent,
  RowComponent,
  embedded,
  tabs,
  actionButtons = [],
  relatedContentFilter = [],
}) => {
  const { formatMessage } = useIntl()
  const { sorting, toggleSorting } = useSort()
  const apiFilters = useGetApiQueryFilters(allowedFilters)
  const [page, pageSize, setPage, setPageSize, resetPage] = usePaginationWithQuery(undefined, [apiFilters])
  const { openDrawer } = useDrawer()
  const { solution } = useAuthentication().scope
  const { productMapping, showAdditionalExportColumns } = useFlags()

  const nonScreenedResponseFilter = createNonScreenedResponseFilter()

  const basePayload = {
    filter: [...relatedContentFilter, nonScreenedResponseFilter],
    sort: sorting,
    include: [
      'creatorOrganization',
      'request.responderOrganization',
      'request.template',
      'request.template.image',
      'request.subscriptions.target.assessments.assessmentTemplate',
      'request.subscriptions.target.country.name',
      'request.inquiries.requestAutomation',
      'request.target.organization',
      'request.subscriptions.target.referralContact',
      'request.target.contacts.user',
      ...(productMapping ? ['request.subscriptions.targetProduct'] : []),
    ],
  }

  const rawExportPayload = {
    ...basePayload,
    include: [...basePayload.include, 'request.target', 'history.creatorUser'],
  }

  if (apiFilters.some(filter => filter.name.endsWith('Level'))) {
    const baselineAssessmentFilter: FilterGroup = {
      name: 'request.template.tags.tag',
      filters: [
        {
          value: WfRequestTags.PublicRequest3,
          operator: Operators.EqualTo,
        },
      ],
    }
    basePayload.filter.push(baselineAssessmentFilter)
  }

  // Enhance filters with verification status when appropriate (pure function call)
  const enhancedFilters = enhanceFiltersWithVerificationStatus(apiFilters)

  const payload = {
    ...basePayload,
    include: [...basePayload.include],
    filter: [...basePayload.filter, ...enhancedFilters],
    pagination: {
      pageNumber: page,
      itemsPerPage: pageSize,
    },
  }

  const { items, count, isLoading, isFetching, isError } = useFetchCollectionWithPost<Response>({
    key: FetchKey.Response,
    endpoint: endpoints.responsesCollection,
    payload,
  })

  const showTargetProductColumn = useMemo(
    () => productMapping && items.some(response => response.request.targetProduct),
    [items, productMapping],
  )

  return (
    <>
      <ResourceCollectionScene
        title={formatMessage({ id: 'navbar.manageRequests' })}
        tabs={tabs}
        actionButtons={[
          {
            label: formatMessage({ id: 'resourceCollections.general.export' }),
            variant: 'outlined',
            onClick: () =>
              openDrawer(
                <DrawerViewExport
                  resourceType={ResourceTypes.Response}
                  resourceView={tabs?.activeTabParam}
                  count={count}
                  userFilter={apiFilters}
                  exportColumns={getResponseExportColumnsAccessor(
                    solution,
                    false,
                    productMapping,
                    showAdditionalExportColumns,
                  )}
                  rawExportPayload={rawExportPayload}
                  onlyLatest
                />,
              ),
          },
          ...actionButtons,
        ]}
        filter={
          <ResponsesFilters
            implicitFilters={[...relatedContentFilter, nonScreenedResponseFilter]}
            allowedFilters={allowedFilters}
            resetPage={resetPage}
          />
        }
        embedded={embedded}
      >
        <Table<Response>
          RowComponent={RowComponent}
          rowComponentProps={{ showTargetProductColumn }}
          HeaderComponent={() => (
            <HeaderComponent
              toggleSorting={toggleSorting}
              activeSorting={sorting}
              showTargetProductColumn={showTargetProductColumn}
            />
          )}
          data={items}
          isLoading={isLoading || isFetching}
          count={count}
          isError={isError}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          stickyColumnIndex={2}
        />
      </ResourceCollectionScene>
    </>
  )
}

export default BaseAccessorResponseScene
